import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, FlaskConical, Loader2, Lock, Search, Upload, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { ORG_BADGE, ORG_LABEL, fmtDate, fmtDateTime } from "@/lib/constants";
import { AIUTI } from "@/lib/guide";
import { Hint } from "@/components/Guida";
import { PmBot } from "@/components/PmBot";

export default function AreaTest() {
  const { user } = useAuth();
  const { projectId } = useProject();
  const [plan, setPlan] = useState(null);
  const [sprint, setSprint] = useState("all");
  const [q, setQ] = useState("");
  const [note, setNote] = useState({});
  const [allegati, setAllegati] = useState({});
  const [busy, setBusy] = useState("");
  const [testlist, setTestlist] = useState([]);
  const [tlSprint, setTlSprint] = useState("");
  const [tlFile, setTlFile] = useState(null);

  const load = () =>
    api.get(`/projects/${projectId}/plan`).then(({ data }) => setPlan(data))
      .catch((e) => toast.error(formatApiError(e)));

  const caricaTestlist = () =>
    api.get(`/projects/${projectId}/testlist`).then(({ data }) => setTestlist(data)).catch(() => setTestlist([]));

  useEffect(() => {
    if (!projectId) return;
    setPlan(null);
    load();
    caricaTestlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const canTest = user.role === "tester" || user.role === "admin";
  const bloccato = (t) => (user.role === "admin" ? null : t.bloccato_da || null);

  const inTest = useMemo(() => {
    if (!plan) return [];
    return plan.tasks.filter(
      (t) =>
        t.stato === "In Test" &&
        (sprint === "all" || t.sprint === sprint) &&
        (!q || `${t.codice} ${t.task}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [plan, sprint, q]);

  const attivitaTest = useMemo(() => {
    if (!plan) return [];
    return plan.tasks.filter(
      (t) =>
        t.tipo === "Test" &&
        (sprint === "all" || t.sprint === sprint) &&
        (!q || `${t.codice} ${t.task}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [plan, sprint, q]);

  const testati = useMemo(
    () => (plan ? plan.tasks.filter((t) => t.esito_test).slice(0, 25) : []),
    [plan]
  );

  const caricaFileTestlist = async () => {
    if (!tlSprint || !tlFile) {
      toast.error("Scegli lo sprint e il file della test list");
      return;
    }
    setBusy("testlist");
    try {
      const fd = new FormData();
      fd.append("sprint", tlSprint);
      fd.append("file", tlFile);
      await api.post(`/projects/${projectId}/testlist`, fd);
      toast.success(`Test list di ${tlSprint} allegata`);
      setTlFile(null);
      caricaTestlist();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy("");
  };

  const scaricaDoc = async (id, nome) => {
    try {
      await downloadFile(`/documents/${id}/download`, nome || "documento");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const daiEsito = async (task, esito) => {
    const noteTask = (note[task.id] || "").trim();
    const file = allegati[task.id];
    if (esito === "KO" && !noteTask) {
      toast.error("Per un KO descrivi il bug nelle note");
      return;
    }
    if (esito === "KO" && !file) {
      toast.error("Per un KO è obbligatorio allegare un file (screenshot, log o report)");
      return;
    }
    setBusy(task.id);
    try {
      const fd = new FormData();
      fd.append("esito", esito);
      fd.append("note", noteTask);
      if (file) fd.append("file", file);
      await api.post(`/tasks/${task.id}/esito`, fd);
      setAllegati((a) => ({ ...a, [task.id]: null }));
      toast.success(
        esito === "OK"
          ? `${task.codice} approvato: task completato al 100%`
          : `${task.codice} respinto: torna in lavorazione al Dev`
      );
      setNote((n) => ({ ...n, [task.id]: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy("");
  };

  const aggiornaAttivita = async (task, patch) => {
    setBusy(task.id);
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, patch);
      setPlan((p) => ({ ...p, tasks: p.tasks.map((t) => (t.id === data.id ? { ...t, ...data } : t)) }));
      toast.success(`${task.codice} aggiornato`);
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    }
    setBusy("");
  };

  if (!plan)
    return (
      <div className="grid h-64 place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="area-test-page">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">{plan.project.nome}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">Area Test</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-500 md:text-base">
          {canTest
            ? "In alto i task consegnati in stato In Test: dai OK per approvare o KO per rimandarli in lavorazione, con le note di test. Più in basso trovi 'Le attività di test' del piano, che aggiorni solo tu."
            : "Task consegnati al test: in attesa dell'esito del tester."}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative min-w-[190px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice o task..."
                 className="h-10 pl-9" data-testid="test-search-input" />
        </div>
        <Select value={sprint} onValueChange={setSprint}>
          <SelectTrigger className="h-10 w-full sm:w-[190px]" data-testid="test-sprint-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Tutti gli sprint</SelectItem>
            {plan.sprints.map((s) => (<SelectItem key={s.key} value={s.key}>{s.nome}</SelectItem>))}
          </SelectContent>
        </Select>
        <span className="font-mono text-xs text-slate-500" data-testid="test-count">{inTest.length} task in test</span>
      </div>


      {(user.role === "tester" || user.role === "pm" || user.role === "admin") && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-5" data-testid="testlist-card">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-600">
            Test list di fine sprint
            <Hint id="testlist" testo={AIUTI.testlist} />
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-slate-600">
            La test list chiude lo sprint ed è scaricabile solo da Tester e PM.
          </p>

          {canTest && (
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <Select value={tlSprint} onValueChange={setTlSprint}>
                <SelectTrigger className="h-10 w-full bg-white sm:w-56" data-testid="testlist-sprint-select">
                  <SelectValue placeholder="Scegli lo sprint" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {plan.sprints.map((s) => (<SelectItem key={s.key} value={s.key}>{s.nome}</SelectItem>))}
                </SelectContent>
              </Select>
              <input
                type="file"
                onChange={(e) => setTlFile(e.target.files?.[0] || null)}
                data-testid="testlist-file-input"
                className="w-full cursor-pointer rounded-lg border border-slate-200 bg-white p-2 text-xs file:mr-2 file:rounded file:border-0 file:bg-slate-900 file:px-2.5 file:py-1 file:text-xs file:text-white sm:w-auto"
              />
              <Button onClick={caricaFileTestlist} disabled={busy === "testlist"}
                      className="h-10 bg-violet-600 text-white hover:bg-violet-500"
                      data-testid="testlist-upload-button">
                {busy === "testlist" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Allega test list
              </Button>
            </div>
          )}

          {testlist.length ? (
            <div className="mt-4 space-y-2" data-testid="testlist-list">
              {testlist.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-xs"
                     data-testid={`testlist-item-${d.id}`}>
                  <span className="font-medium text-slate-800">{d.titolo}</span>
                  <span className="font-mono text-slate-400">{d.created_by} · {fmtDateTime(d.created_at)}</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => scaricaDoc(d.id, d.original_filename)}
                          data-testid={`testlist-download-${d.id}`}>
                    <Download className="mr-1.5 h-3 w-3" /> Scarica
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-400" data-testid="testlist-empty">
              Nessuna test list allegata finora.
            </p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {inTest.map((t) => (
          <div key={t.id} data-testid={`test-task-${t.codice}`}
               className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm transition-colors hover:border-amber-300">
            <div className="grid gap-5 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-slate-900 px-2 py-0.5 font-mono text-xs font-semibold text-white">{t.codice}</span>
                  <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] ${ORG_BADGE[t.org]}`}>
                    {ORG_LABEL[t.org]}
                  </span>
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-mono text-[10px] text-amber-700">
                    In Test
                  </span>
                  {t.rework_count > 0 && (
                    <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 font-mono text-[10px] text-rose-700">
                      RW {t.rework_count}
                    </span>
                  )}
                </div>
                <p className="mt-2 font-medium leading-snug text-slate-900">{t.task}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {t.assegnatario || "Non assegnato"} · {fmtDate(t.data_inizio)} → {fmtDate(t.data_fine)} · {t.stima} gg
                </p>
                {t.note && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{t.note}</p>}
                {bloccato(t) && (
                  <p className="mt-2 flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700"
                     data-testid={`test-sprint-locked-${t.codice}`}>
                    <Lock className="h-3 w-3" /> {bloccato(t)} non è ancora tutto in test: non puoi validare questo task
                  </p>
                )}
              </div>

              <div className="lg:col-span-4">
                <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-600">
                  Note di test
                  <Hint id="note-test" testo={AIUTI.noteTest} />
                </label>
                <Textarea
                  rows={3}
                  disabled={!canTest || !!bloccato(t)}
                  value={note[t.id] ?? t.note_test ?? ""}
                  onChange={(e) => setNote((n) => ({ ...n, [t.id]: e.target.value }))}
                  placeholder="Cosa hai verificato, cosa non funziona..."
                  className="mt-1.5 resize-none text-sm"
                  data-testid={`test-note-input-${t.codice}`}
                />
                <label className="mt-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-rose-600">
                  Allegato (obbligatorio per il KO)
                  <Hint id="allegato-ko" testo={AIUTI.allegatoKo} />
                </label>
                <input
                  type="file"
                  disabled={!canTest || !!bloccato(t)}
                  onChange={(e) => setAllegati((a) => ({ ...a, [t.id]: e.target.files?.[0] || null }))}
                  data-testid={`test-file-input-${t.codice}`}
                  className="mt-1.5 w-full cursor-pointer rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs file:mr-2 file:rounded file:border-0 file:bg-slate-900 file:px-2.5 file:py-1 file:text-xs file:text-white"
                />
                {allegati[t.id] && (
                  <p className="mt-1 truncate font-mono text-[11px] text-emerald-700" data-testid={`test-file-name-${t.codice}`}>
                    {allegati[t.id].name}
                  </p>
                )}
              </div>

              <div className="flex flex-col justify-center gap-2 lg:col-span-3">
                <Button
                  onClick={() => daiEsito(t, "OK")}
                  disabled={!canTest || !!bloccato(t) || busy === t.id}
                  className="h-10 bg-emerald-600 text-white hover:bg-emerald-500"
                  data-testid={`test-ok-${t.codice}`}
                >
                  {busy === t.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                  OK · approva
                </Button>
                <Button
                  onClick={() => daiEsito(t, "KO")}
                  disabled={!canTest || !!bloccato(t) || busy === t.id}
                  variant="outline"
                  className="h-10 border-rose-300 text-rose-700 hover:bg-rose-600 hover:text-white"
                  data-testid={`test-ko-${t.codice}`}
                >
                  <XCircle className="mr-2 h-4 w-4" /> KO · rimanda al Dev
                </Button>
              </div>
            </div>
          </div>
        ))}

        {inTest.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="test-empty">
            <FlaskConical className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">Nessun task in attesa di test.</p>
          </div>
        )}
      </div>

      {attivitaTest.length > 0 && (
        <div className="rounded-2xl border border-violet-200 bg-white p-5 shadow-sm" data-testid="attivita-test-card">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Le attività di test</h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Sono le attività di test del piano: solo il tester le aggiorna, il Dev le vede in sola lettura.
          </p>
          <div className="mt-4 space-y-3">
            {attivitaTest.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 p-4"
                   data-testid={`attivita-test-${t.codice}`}>
                <div className="grid gap-4 lg:grid-cols-12">
                  <div className="lg:col-span-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded bg-violet-700 px-2 py-0.5 font-mono text-xs font-semibold text-white">{t.codice}</span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[10px] text-slate-600">
                        {t.sprint}
                      </span>
                    </div>
                    <p className="mt-2 font-medium leading-snug text-slate-900">{t.task}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {t.assegnatario || "Non assegnato"} · {fmtDate(t.data_inizio)} → {fmtDate(t.data_fine)} · {t.stima} gg
                    </p>
                  </div>
                  <div className="lg:col-span-4">
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Avanzamento
                      </span>
                      <span className="font-mono text-sm font-semibold text-slate-900"
                            data-testid={`attivita-test-perc-${t.codice}`}>{t.percentuale_cliente}%</span>
                    </div>
                    <input type="range" min={0} max={100} step={5} value={t.percentuale_cliente}
                           disabled={!canTest || busy === t.id}
                           onChange={(e) => setPlan((p) => ({
                             ...p,
                             tasks: p.tasks.map((x) => (x.id === t.id ? { ...x, percentuale_cliente: Number(e.target.value) } : x)),
                           }))}
                           onMouseUp={(e) => aggiornaAttivita(t, { percentuale_cliente: Number(e.target.value) })}
                           onTouchEnd={(e) => aggiornaAttivita(t, { percentuale_cliente: Number(e.target.value) })}
                           onKeyUp={(e) => aggiornaAttivita(t, { percentuale_cliente: Number(e.target.value) })}
                           className="mt-2 w-full accent-violet-600"
                           data-testid={`attivita-test-slider-${t.codice}`} />
                  </div>
                  <div className="lg:col-span-3">
                    <Select value={t.stato} disabled={!canTest || busy === t.id}
                            onValueChange={(v) => aggiornaAttivita(t, { stato: v })}>
                      <SelectTrigger className="h-10" data-testid={`attivita-test-stato-${t.codice}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {["Da iniziare", "In Progress", "In Test", "OK"].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {testati.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="test-history-card">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Esiti già dati</h2>
          <div className="mt-3 divide-y divide-slate-100">
            {testati.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-2 py-2.5 text-sm" data-testid={`test-result-${t.codice}`}>
                <span className="rounded bg-slate-900 px-2 py-0.5 font-mono text-[11px] text-white">{t.codice}</span>
                <span
                  className={`rounded-full border px-2 py-0.5 font-mono text-[10px] ${
                    t.esito_test === "OK"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-rose-200 bg-rose-50 text-rose-700"
                  }`}
                >
                  {t.esito_test}
                </span>
                <span className="min-w-0 flex-1 truncate text-slate-700">{t.task}</span>
                <span className="font-mono text-[11px] text-slate-400">
                  {t.testato_da} · {fmtDateTime(t.testato_il)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <PmBot pagina="test" sprint={sprint === "all" ? null : sprint} />
    </div>
  );
}
